/**
 * Panel admin: features de marketing por nicho × plan — vista matricial.
 *
 * Rubros = dinámicos, vienen de sm_niche_config (botón "Nuevo rubro" da de alta uno).
 * Columnas = tiers del rubro actual — dinámicos, vienen de sm_niche_plan_marketing
 *            (botón "Nuevo plan" agrega una columna; cada columna tiene "Eliminar").
 *            Trial/Starter/Pro/Business/Enterprise son datos existentes, no una
 *            estructura fija — un rubro puede tener más, menos o distintos tiers.
 * Filas    = features del catálogo real, agrupadas por dominio
 *          + columnas inline por fila: label override + show_in_landing
 * Cabecera = precio + límites + label de plan editables por columna
 * Barra superior = enable/disable nicho + descuentos 12/24/48m
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Eye, EyeOff, Pencil, Star, ChevronDown, ChevronRight, Bot, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { SM_FEATURES } from "@/lib/feature-catalog";
import { SM_PLANS } from "@/lib/pricing-catalog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { RENATA_COPILOT_FEATURES } from "@/lib/renata/copilot-features";

// ─── Constantes ───────────────────────────────────────────────────────────────

/** Tiers históricos con defaults conocidos (nombre, precio, límites base, destacado).
 * No es una lista cerrada: un rubro puede tener tiers con cualquier otro id. */
const PLAN_IDS = ["free_trial", "starter", "pro", "business", "enterprise"] as const;
type KnownPlanId = typeof PLAN_IDS[number];
type PlanId = string;

const PLAN_BASE = Object.fromEntries(
  SM_PLANS.filter((p) => (PLAN_IDS as readonly string[]).includes(p.id)).map((p) => [p.id, p]),
) as Record<KnownPlanId, (typeof SM_PLANS)[number]>;

// Grupos de features para la matriz
const FEATURE_GROUPS: { label: string; ids: string[] }[] = (() => {
  const shipping = new Set([
    "shipping_shipnow","shipping_tracking","treggo_shipping","andreani_shipping",
    "enviopack_shipping","cabify_shipping","uber_direct_shipping",
    "delivery_provider_pedidosya","delivery_provider_rapiboy","oca_epak_shipping",
    "correo_argentino_shipping","delivery_provider_shipnow","delivery_tracking",
    "delivery_quotes","delivery_webhooks","delivery_dispatch","delivery_pickup",
    "delivery_eta","zipnova_shipping","pickit_shipping","shipping_multi_carrier",
    "delivery_zones",
  ]);
  const payments = new Set([
    "payment_mercadopago","payment_paypal","payment_online","payment_payway",
    "payment_partial","payment_marketplace_ready","payment_dodo","payment_modo",
    "payment_getnet","payment_uala","payment_nave","payment_talo","payment_sipago",
    "payment_qr","payment_links","payment_recurring","payment_refunds","payment_pos_mode",
    "billing_lemon",
  ]);
  const hardware = new Set([
    "hardware","hardware_pos","hardware_fiscal","hardware_scale","hardware_barcode",
    "hardware_qr","hardware_rfid","hardware_printer","hardware_labels",
    "hardware_cashdrawer","hardware_customer_display","hardware_payment_terminal",
    "hardware_data_collector","hardware_kitchen","hardware_nfc","hardware_usb",
    "hardware_bluetooth","hardware_serial","hardware_network","hardware_custom_drivers",
    "hardware_multi_device","thermal_printing",
  ]);
  const marketing = new Set([
    "integrations_gtm","integrations_tiktok_pixel","integrations_hotjar",
    "integrations_google_ads","integrations_pinterest_tag","integrations_merchant",
    "integrations_google_shopping","integrations_meta","integrations_ga4",
    "integrations_clarity","custom_head_scripts","push_notifications",
    "email_automations","lead_capture","analytics_advanced",
  ]);
  const ops = new Set([
    "produccion_b2b","institutional_orders","inventory_bom","supply_chain",
    "dine_in_tables","table_reservations","inventory_advanced","bulk_prices",
    "operations_suppliers","discount_coupons","promotions_engine","combos",
    "events_tickets","events_qr_validator","api_keys","exports_csv","gift_cards",
    "merchant_quotes","multi_location","invoicing_tusfacturas","invoicing_afip_direct",
    "variants_addons","customer_accounts","loyalty_points","custom_branding",
    "custom_domain","whatsapp_notify","pwa_install",
  ]);

  const servicios = new Set([
    "service_bookings","booking_widget","professionals","service_site_builder",
    "session_packs","waitlist","intake_forms","service_reviews",
    "calendar_sync","commissions",
  ]);

  const buckets: Record<string, string[]> = {
    "Core": [], "Pagos": [], "Logística": [], "Hardware": [],
    "Marketing & Analytics": [], "Servicios y turnos": [], "Operaciones": [], "Otros": [],
  };

  for (const f of SM_FEATURES) {
    if (f.marketingVisible === false) continue;
    if (servicios.has(f.id)) { buckets["Servicios y turnos"].push(f.id); continue; }
    if (payments.has(f.id))  { buckets["Pagos"].push(f.id); continue; }
    if (shipping.has(f.id))  { buckets["Logística"].push(f.id); continue; }
    if (hardware.has(f.id))  { buckets["Hardware"].push(f.id); continue; }
    if (marketing.has(f.id)) { buckets["Marketing & Analytics"].push(f.id); continue; }
    if (ops.has(f.id))       { buckets["Operaciones"].push(f.id); continue; }
    if (["catalog_public","orders_online","pesaje_module","stock_management",
         "widget_embed","digital_products","feature_presets","storefront_experience",
         "product_tags","media","sample_data","product_tours","tenant_support"].includes(f.id))
      { buckets["Core"].push(f.id); continue; }
    buckets["Otros"].push(f.id);
  }

  const byName = (a: string, b: string) => {
    const na = SM_FEATURES.find((f) => f.id === a)?.name ?? a;
    const nb = SM_FEATURES.find((f) => f.id === b)?.name ?? b;
    return na.localeCompare(nb, "es");
  };
  return Object.entries(buckets)
    .filter(([, ids]) => ids.length > 0)
    .map(([label, ids]) => ({ label, ids: ids.sort(byName) }));
})();

const FEATURE_BY_ID = new Map(SM_FEATURES.map((f) => [f.id, f]));

// ─── Types ────────────────────────────────────────────────────────────────────

interface LimitsOverride {
  products?: number | null;
  ordersPerMonth?: number | null;
  locations?: number | null;
  categories?: number | null;
  brands?: number | null;
  b2bUsers?: number | null;
  teamMembers?: number | null;
  images?: number | null;
  emailsPerDay?: number | null;
  pushPerDay?: number | null;
  calendarSync?: number | null;
  /** Nicho "servicios" — portado de Bookings Hub (PlanLimits.professionals). */
  professionals?: number | null;
  /** Nicho "servicios" — portado de Bookings Hub (PlanLimits.bookings_per_month). */
  bookingsPerMonth?: number | null;
  storefront?: boolean;
  portalB2b?: boolean;
  renataCreditsMonth?: number | null;
  renataCreditsExpiryDays?: number | null;
}

interface PlanDraft {
  featureIds: Set<string>;
  priceArs: number | null;
  limits: LimitsOverride;
  planLabel: string;
  highlighted: boolean;
  dirty: boolean;
}

interface NicheConfig {
  enabled: boolean;
  discount12m: number | null;
  discount24m: number | null;
  discount48m: number | null;
  /** Nombre mostrado en la píldora de precios de la landing pública. */
  label: string;
  /** BusinessProfile técnico (business-profile.ts) para el CTA de trial/onboarding de este nicho. */
  businessProfile: string;
  dirty: boolean;
}

/** Config global por feature (label override + visibilidad landing) */
interface FeatureCfg {
  label: string;         // "" = usar nombre del catálogo
  showInLanding: boolean;
  dirty: boolean;
}

type NicheMatrix = Record<PlanId, PlanDraft>;
type FeatureCfgMap = Record<string, FeatureCfg>;

/** copilotFlags[planId][featureId] = boolean */
type CopilotFlagsMatrix = Record<PlanId, Record<string, boolean>>;

interface ApiRow {
  nicheId: string; planId: string; featureIds: string[];
  priceArs: number | null; limitsOverride: LimitsOverride | null;
  planLabel: string | null;
}
interface ApiConfig {
  nicheId: string; enabled: boolean;
  discount12m: number | null; discount24m: number | null; discount48m: number | null;
  label: string | null; businessProfile: string | null;
}
interface ApiFeatureCfg {
  featureId: string; label: string | null; showInLanding: boolean;
}
interface NicheOption { id: string; label: string }

function defaultDraft(planId: PlanId): PlanDraft {
  const base = PLAN_BASE[planId as KnownPlanId];
  return {
    featureIds: new Set(),
    priceArs: base?.priceArs ?? null,
    limits: {},
    planLabel: base?.name ?? planId,
    highlighted: (base as { highlighted?: boolean } | undefined)?.highlighted ?? false,
    dirty: false,
  };
}

function effectiveLimits(planId: PlanId, draft: PlanDraft): LimitsOverride & Record<string, unknown> {
  const base = (PLAN_BASE[planId as KnownPlanId]?.limits ?? {}) as Record<string, unknown>;
  const d = draft.limits as Record<string, unknown>;
  const merged: Record<string, unknown> = {};
  const keys: (keyof LimitsOverride)[] = [
    "products","ordersPerMonth","locations","categories","brands","b2bUsers",
    "teamMembers","images","emailsPerDay","pushPerDay","calendarSync",
    "professionals","bookingsPerMonth",
    "storefront","portalB2b","renataCreditsMonth","renataCreditsExpiryDays",
  ];
  for (const k of keys) {
    merged[k] = d[k] !== undefined ? d[k] : (base[k] ?? null);
  }
  return merged as LimitsOverride & Record<string, unknown>;
}

const numStr = (v: number | null | undefined) => (v == null ? "" : String(v));
const slugify = (value: string) =>
  value.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");

// ─── Component ────────────────────────────────────────────────────────────────

export function AdminNichePlanFeaturesPanel() {
  const [nicheId, setNicheId] = useState<string>("restaurante");
  const [nicheOptions, setNicheOptions] = useState<NicheOption[]>([]);
  const [tierIds, setTierIds] = useState<PlanId[]>([]);
  /** Tiers agregados en esta sesión que todavía no existen en la base — al borrarlos no hace falta pegarle al DELETE. */
  const [unsavedTierIds, setUnsavedTierIds] = useState<Set<PlanId>>(new Set());

  const [matrix, setMatrix] = useState<NicheMatrix>({});
  const [nicheConfig, setNicheConfig] = useState<NicheConfig>({
    enabled: true, discount12m: null, discount24m: null, discount48m: null,
    label: "", businessProfile: "", dirty: false,
  });
  const [allConfigs, setAllConfigs] = useState<Record<string, { enabled: boolean }>>({});
  const [featureCfgs, setFeatureCfgs] = useState<FeatureCfgMap>({});
  const [copilotFlags, setCopilotFlags] = useState<CopilotFlagsMatrix>({});
  const [copilotDirty, setCopilotDirty] = useState<Set<PlanId>>(new Set());
  const [savingCopilot, setSavingCopilot] = useState<PlanId | null>(null);
  const [savingFeature, setSavingFeature] = useState<string | null>(null);
  const [savingHighlighted, setSavingHighlighted] = useState<PlanId | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState<PlanId | "config" | null>(null);
  const [deletingTier, setDeletingTier] = useState<PlanId | null>(null);
  const loadedNiche = useRef<string>("");

  // ── Alta de rubro nuevo ───────────────────────────────────────────────────
  const [showNicheForm, setShowNicheForm] = useState(false);
  const [nicheForm, setNicheForm] = useState({ id: "", label: "", businessProfile: "" });
  const [creatingNiche, setCreatingNiche] = useState(false);

  // ── Load niche ────────────────────────────────────────────────────────────

  const loadNiche = useCallback(async (nid: string) => {
    setLoading(true);
    try {
      const [nicheData, cfgData, plansData, copilotData] = await Promise.all([
        apiGetJson<{ rows: ApiRow[]; config: ApiConfig | null; configs: ApiConfig[] }>(
          `/api/admin/niche-plan-features?niche=${encodeURIComponent(nid)}`,
        ),
        apiGetJson<{ configs: ApiFeatureCfg[] }>("/api/admin/feature-config"),
        apiGetJson<{ plans: Array<{ id: string; highlighted: boolean }> }>("/api/admin/plans").catch(() => ({ plans: [] as Array<{ id: string; highlighted: boolean }> })),
        apiGetJson<{ flags: Record<string, Record<string, boolean>> }>(`/api/admin/renata-flags?niche=${encodeURIComponent(nid)}`).catch(() => ({ flags: {} as Record<string, Record<string, boolean>> })),
      ]);
      const highlightedMap = Object.fromEntries(plansData.plans.map((p) => [p.id, p.highlighted]));

      // Rubros disponibles — dinámico, viene de sm_niche_config (no más lista fija).
      setNicheOptions(nicheData.configs.map((c) => ({ id: c.nicheId, label: c.label || c.nicheId })));

      const configMap: Record<string, { enabled: boolean }> = {};
      for (const c of nicheData.configs) configMap[c.nicheId] = { enabled: c.enabled };
      setAllConfigs(configMap);

      const cfg = nicheData.config;
      setNicheConfig({
        enabled: cfg?.enabled ?? true,
        discount12m: cfg?.discount12m ?? null,
        discount24m: cfg?.discount24m ?? null,
        discount48m: cfg?.discount48m ?? null,
        label: cfg?.label ?? "",
        businessProfile: cfg?.businessProfile ?? "",
        dirty: false,
      });

      // Tiers del rubro — dinámicos, en el orden real (sort_order) que devuelve la API.
      const ids = nicheData.rows.map((row) => row.planId);
      setTierIds(ids);
      setUnsavedTierIds(new Set());
      setMatrix(() => {
        const next: NicheMatrix = {};
        for (const row of nicheData.rows) {
          const pid = row.planId;
          const base = defaultDraft(pid);
          next[pid] = {
            featureIds: new Set(row.featureIds),
            priceArs: row.priceArs ?? base.priceArs,
            limits: row.limitsOverride ?? {},
            planLabel: row.planLabel ?? base.planLabel,
            highlighted: highlightedMap[pid] ?? false,
            dirty: false,
          };
        }
        return next;
      });

      // Feature configs globales
      setFeatureCfgs(() => {
        const map: FeatureCfgMap = {};
        for (const c of cfgData.configs) {
          map[c.featureId] = { label: c.label ?? "", showInLanding: c.showInLanding, dirty: false };
        }
        return map;
      });

      // Copilot flags
      const flagsMap = copilotData.flags as Record<string, Record<string, boolean>>;
      setCopilotFlags(Object.fromEntries(ids.map((pid) => [pid, flagsMap[pid] ?? {}])));
      setCopilotDirty(new Set());

      loadedNiche.current = nid;
    } catch {
      toast.error("No se pudieron cargar los datos del nicho");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadNiche(nicheId); }, [nicheId, loadNiche]);

  // ── Alta de rubro ─────────────────────────────────────────────────────────

  async function createNiche() {
    const id = slugify(nicheForm.id);
    if (!id) { toast.error("El id del rubro es obligatorio (ej: veterinaria)"); return; }
    if (nicheOptions.some((n) => n.id === id)) { toast.error("Ya existe un rubro con ese id"); return; }
    setCreatingNiche(true);
    try {
      await apiSendJson("/api/admin/niche-plan-features", "PATCH", {
        nicheId: id,
        enabled: true,
        label: nicheForm.label.trim() || id,
        businessProfile: nicheForm.businessProfile.trim() || null,
      });
      toast.success("Rubro creado — agregale al menos un plan y publicalo en el menú público");
      setNicheOptions((prev) => [...prev, { id, label: nicheForm.label.trim() || id }]);
      setShowNicheForm(false);
      setNicheForm({ id: "", label: "", businessProfile: "" });
      setNicheId(id);
    } catch {
      toast.error("No se pudo crear el rubro");
    } finally {
      setCreatingNiche(false);
    }
  }

  // ── Niche config handlers ─────────────────────────────────────────────────

  function setConfigField(field: keyof Omit<NicheConfig, "dirty">, value: unknown) {
    setNicheConfig((prev) => ({ ...prev, [field]: value, dirty: true }));
  }

  async function saveConfig() {
    setSaving("config");
    try {
      await apiSendJson("/api/admin/niche-plan-features", "PATCH", {
        nicheId,
        enabled: nicheConfig.enabled,
        discount12m: nicheConfig.discount12m,
        discount24m: nicheConfig.discount24m,
        discount48m: nicheConfig.discount48m,
        label: nicheConfig.label.trim() || null,
        businessProfile: nicheConfig.businessProfile.trim() || null,
      });
      setNicheConfig((prev) => ({ ...prev, dirty: false }));
      setAllConfigs((prev) => ({ ...prev, [nicheId]: { enabled: nicheConfig.enabled } }));
      setNicheOptions((prev) => prev.map((n) => (n.id === nicheId ? { ...n, label: nicheConfig.label.trim() || n.id } : n)));
      toast.success("Configuración del nicho guardada");
    } catch {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(null);
    }
  }

  // ── Feature config handlers ───────────────────────────────────────────────

  function setFeatureCfgField(featureId: string, field: "label" | "showInLanding", value: unknown) {
    setFeatureCfgs((prev) => ({
      ...prev,
      [featureId]: {
        ...prev[featureId],
        label: prev[featureId]?.label ?? "",
        showInLanding: prev[featureId]?.showInLanding ?? true,
        [field]: value,
        dirty: true,
      },
    }));
  }

  async function saveFeatureCfg(featureId: string) {
    setSavingFeature(featureId);
    try {
      const cfg = featureCfgs[featureId];
      await apiSendJson("/api/admin/feature-config", "PATCH", {
        featureId,
        label: cfg?.label || null,
        showInLanding: cfg?.showInLanding ?? true,
      });
      setFeatureCfgs((prev) => ({ ...prev, [featureId]: { ...prev[featureId], dirty: false } }));
      toast.success(`Feature "${featureId}" actualizada`);
    } catch {
      toast.error("Error al guardar feature");
    } finally {
      setSavingFeature(null);
    }
  }

  // ── Plan matrix handlers ──────────────────────────────────────────────────

  function toggleFeature(planId: PlanId, featureId: string) {
    setMatrix((prev) => {
      const draft = { ...prev[planId], featureIds: new Set(prev[planId].featureIds), dirty: true };
      if (draft.featureIds.has(featureId)) draft.featureIds.delete(featureId);
      else draft.featureIds.add(featureId);
      return { ...prev, [planId]: draft };
    });
  }

  function setPlanPrice(planId: PlanId, value: string) {
    setMatrix((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], priceArs: value ? Number(value) : null, dirty: true },
    }));
  }

  function setPlanLabel(planId: PlanId, value: string) {
    setMatrix((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], planLabel: value, dirty: true },
    }));
  }

  function setPlanLimit(planId: PlanId, field: keyof LimitsOverride, value: unknown) {
    setMatrix((prev) => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        limits: { ...prev[planId].limits, [field]: value },
        dirty: true,
      },
    }));
  }

  async function saveHighlighted(planId: PlanId) {
    const current = matrix[planId].highlighted;
    const next = !current;
    setSavingHighlighted(planId);
    setMatrix((prev) => ({ ...prev, [planId]: { ...prev[planId], highlighted: next } }));
    try {
      await apiSendJson("/api/admin/plans", "PATCH", { id: planId, highlighted: next });
      toast.success(next ? "Plan marcado como destacado" : "Destacado removido");
    } catch {
      setMatrix((prev) => ({ ...prev, [planId]: { ...prev[planId], highlighted: current } }));
      toast.error("Error al guardar destacado");
    } finally {
      setSavingHighlighted(null);
    }
  }

  function toggleSection(label: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  }

  function collapseAll() {
    const all = new Set(["Límites & cuotas", "COPILOT / RENATA", ...FEATURE_GROUPS.map((g) => g.label)]);
    setCollapsedSections(all);
  }

  function expandAll() {
    setCollapsedSections(new Set());
  }

  function toggleCopilotFlag(planId: PlanId, featureId: string) {
    setCopilotFlags((prev) => {
      const current = (prev[planId] ?? {})[featureId] !== false; // default true
      return { ...prev, [planId]: { ...prev[planId], [featureId]: !current } };
    });
    setCopilotDirty((prev) => new Set(prev).add(planId));
  }

  async function saveCopilotPlan(planId: PlanId) {
    setSavingCopilot(planId);
    try {
      await apiSendJson("/api/admin/renata-flags", "PUT", {
        nicheId,
        planId,
        flags: copilotFlags[planId] ?? {},
      });
      setCopilotDirty((prev) => { const next = new Set(prev); next.delete(planId); return next; });
      toast.success(`Flags Renata guardados: ${matrix[planId]?.planLabel ?? planId}`);
    } catch {
      toast.error("Error al guardar flags de Renata");
    } finally {
      setSavingCopilot(null);
    }
  }

  async function savePlan(planId: PlanId) {
    setSaving(planId);
    try {
      const draft = matrix[planId];
      const base = PLAN_BASE[planId as KnownPlanId];
      await apiSendJson("/api/admin/niche-plan-features", "PUT", {
        nicheId, planId,
        featureIds: [...draft.featureIds],
        priceArs: draft.priceArs,
        limitsOverride: Object.keys(draft.limits).length ? draft.limits : null,
        planLabel: draft.planLabel !== base?.name ? draft.planLabel : null,
      });
      // Contrato core global (sm_plans): highlighted ya se escribe vía PATCH al editar la celda.
      // limits_override de nicho se aplica en runtime (dbSmGetPlanLimitsForTenant) — no se
      // vuelca a sm_plans para no pisar el contrato entre rubros.
      setMatrix((prev) => ({ ...prev, [planId]: { ...prev[planId], dirty: false } }));
      setUnsavedTierIds((prev) => { const next = new Set(prev); next.delete(planId); return next; });
      toast.success(`Guardado: ${draft.planLabel}`);
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(null);
    }
  }

  /** Agrega una columna de plan nueva — vacía, sin persistir hasta el primer "Guardar". */
  function addTier() {
    const base = "nuevo-plan";
    let id = base;
    let i = 2;
    while (tierIds.includes(id)) id = `${base}-${i++}`;
    setTierIds((prev) => [...prev, id]);
    setUnsavedTierIds((prev) => new Set(prev).add(id));
    setMatrix((prev) => ({ ...prev, [id]: { ...defaultDraft(id), planLabel: "Nuevo plan", priceArs: null, dirty: true } }));
    setCopilotFlags((prev) => ({ ...prev, [id]: {} }));
  }

  /** Borra una columna de plan — si nunca se guardó, solo local; si ya existía, pega al DELETE real. */
  async function removeTier(planId: PlanId) {
    if (tierIds.length <= 1) { toast.error("El rubro debe conservar al menos un plan"); return; }
    const label = matrix[planId]?.planLabel ?? planId;
    if (!window.confirm(`¿Eliminar el plan "${label}"? Esta acción no se puede deshacer.`)) return;
    const isUnsaved = unsavedTierIds.has(planId);
    setDeletingTier(planId);
    try {
      if (!isUnsaved) {
        await apiSendJson("/api/admin/niche-plan-features", "DELETE", { nicheId, planId });
      }
      setTierIds((prev) => prev.filter((id) => id !== planId));
      setMatrix((prev) => { const next = { ...prev }; delete next[planId]; return next; });
      setUnsavedTierIds((prev) => { const next = new Set(prev); next.delete(planId); return next; });
      toast.success(`Plan "${label}" eliminado`);
    } catch {
      toast.error("No se pudo eliminar el plan");
    } finally {
      setDeletingTier(null);
    }
  }

  async function saveAll() {
    const tasks: Promise<void>[] = [];
    for (const pid of tierIds) { if (matrix[pid]?.dirty) tasks.push(savePlan(pid)); }
    if (nicheConfig.dirty) tasks.push(saveConfig());
    for (const fid of Object.keys(featureCfgs)) {
      if (featureCfgs[fid].dirty) tasks.push(saveFeatureCfg(fid));
    }
    for (const pid of Array.from(copilotDirty)) tasks.push(saveCopilotPlan(pid));
    if (!tasks.length) { toast.info("Sin cambios pendientes"); return; }
    await Promise.all(tasks);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  const niche = nicheOptions.find((n) => n.id === nicheId) ?? { id: nicheId, label: nicheId };
  const dirtyFeatures = Object.values(featureCfgs).filter((c) => c.dirty).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">
            Define precio, límites, descuentos y features de marketing por rubro × plan.
            Rubros y planes son dinámicos: "Nuevo rubro" y "Nuevo plan" agregan columnas/pestañas reales.
            El label y la visibilidad de cada feature es global (no por nicho).
          </p>
          <div className="mt-1.5 flex gap-2">
            <button type="button" onClick={expandAll} className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
              Expandir todo
            </button>
            <span className="text-muted-foreground/30">·</span>
            <button type="button" onClick={collapseAll} className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
              Colapsar todo
            </button>
          </div>
        </div>
        <button
          type="button" onClick={() => void saveAll()}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-4 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:opacity-90"
        >
          <Save className="h-3.5 w-3.5" />
          Guardar todo
          {dirtyFeatures > 0 && (
            <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-bold text-black">
              {dirtyFeatures}
            </span>
          )}
        </button>
      </div>

      {/* Niche tabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        {nicheOptions.map((n) => {
          const enabled = allConfigs[n.id]?.enabled !== false;
          return (
            <button
              key={n.id} type="button" onClick={() => setNicheId(n.id)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors",
                nicheId === n.id
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border/50 text-muted-foreground hover:border-primary/30 hover:text-foreground",
                !enabled && "opacity-40",
              )}
            >
              {!enabled && <EyeOff className="h-3 w-3" />}
              {n.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowNicheForm((v) => !v)}
          className="flex items-center gap-1 rounded-full border border-dashed border-primary/40 px-3 py-1 text-[12px] font-medium text-primary hover:bg-primary/10"
        >
          <Plus className="h-3 w-3" /> Nuevo rubro
        </button>
      </div>

      {showNicheForm && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Id del rubro</span>
            <input
              type="text" value={nicheForm.id} placeholder="ej: veterinaria"
              onChange={(e) => setNicheForm((f) => ({ ...f, id: e.target.value }))}
              className="w-40 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Nombre en la landing</span>
            <input
              type="text" value={nicheForm.label} placeholder="ej: Veterinarias"
              onChange={(e) => setNicheForm((f) => ({ ...f, label: e.target.value }))}
              className="w-48 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">BusinessProfile</span>
            <input
              type="text" value={nicheForm.businessProfile} placeholder="ej: veterinaria"
              onChange={(e) => setNicheForm((f) => ({ ...f, businessProfile: e.target.value }))}
              className="w-40 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/40"
            />
          </div>
          <button
            type="button" onClick={() => void createNiche()} disabled={creatingNiche}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {creatingNiche ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Crear rubro
          </button>
          <button type="button" onClick={() => setShowNicheForm(false)} className="text-[12px] text-muted-foreground hover:text-foreground">
            Cancelar
          </button>
        </div>
      )}

      {/* Niche config bar */}
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-border/40 bg-card/30 px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Visibilidad del nicho
          </span>
          <select
            value={nicheConfig.enabled ? "visible" : "oculto"}
            onChange={(e) => setConfigField("enabled", e.target.value === "visible")}
            className="rounded-lg border border-border/50 bg-background px-3 py-1.5 text-[12.5px] text-foreground"
          >
            <option value="visible">Visible en landing</option>
            <option value="oculto">Oculto / deshabilitado</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Nombre en la landing
          </span>
          <input
            type="text"
            value={nicheConfig.label}
            placeholder={niche.label}
            onChange={(e) => setConfigField("label", e.target.value)}
            className="w-44 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/40"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            BusinessProfile (trial/onboarding)
          </span>
          <input
            type="text"
            value={nicheConfig.businessProfile}
            placeholder="ej: restaurant, market, servicios"
            onChange={(e) => setConfigField("businessProfile", e.target.value)}
            className="w-52 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/40"
          />
        </div>

        {(["12", "24", "48"] as const).map((months) => {
          const field = `discount${months}m` as "discount12m" | "discount24m" | "discount48m";
          return (
            <div key={months} className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Desc. {months} meses
              </span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={0} max={80} step={1}
                  value={numStr(nicheConfig[field])}
                  placeholder="—"
                  onChange={(e) => setConfigField(field, e.target.value ? Number(e.target.value) : null)}
                  className="w-16 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/40"
                />
                <span className="text-[12px] text-muted-foreground">%</span>
              </div>
            </div>
          );
        })}

        {nicheConfig.dirty && (
          <button
            type="button" onClick={() => void saveConfig()}
            disabled={saving === "config"}
            className="flex items-center gap-1.5 self-end rounded-lg bg-amber-500/20 px-3 py-1.5 text-[12px] font-semibold text-amber-400 hover:bg-amber-500/30"
          >
            {saving === "config" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Guardar config {niche.label}
          </button>
        )}
      </div>

      {/* Matriz */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : tierIds.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border/60 p-10 text-center">
          <p className="text-sm text-muted-foreground">Este rubro todavía no tiene planes.</p>
          <button
            type="button" onClick={addTier}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> Agregar el primer plan
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/50">
          <table className="w-full text-[12.5px]" style={{ minWidth: "900px" }}>
            {/* Cabecera: nombre + precio + guardar por plan */}
            <thead>
              <tr className="border-b border-border/40 bg-card/60">
                <th className="sticky left-0 z-10 w-[200px] bg-card px-3 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground shadow-[4px_0_8px_-4px_rgba(0,0,0,.15)]">
                  <div className="flex items-center justify-between gap-1">
                    Límite / Feature
                    <button
                      type="button" onClick={addTier} title="Agregar plan"
                      className="rounded p-0.5 text-muted-foreground/60 hover:bg-primary/10 hover:text-primary"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </th>
                {tierIds.map((pid) => {
                  const draft = matrix[pid];
                  const isTrial = pid === "free_trial";
                  const isKnownPlan = PLAN_BASE[pid as KnownPlanId] != null;
                  return (
                    <th key={pid} className="min-w-[140px] border-l border-border/30 px-3 py-3 text-left align-top">
                      <div className="flex items-center gap-1">
                        <input
                          value={draft.planLabel}
                          onChange={(e) => setPlanLabel(pid, e.target.value)}
                          className="w-full rounded border border-border/30 bg-transparent px-1 py-0.5 text-[13px] font-bold text-foreground focus:border-primary/50 focus:outline-none"
                        />
                        {draft.dirty && (
                          <span className="shrink-0 rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-amber-400">•</span>
                        )}
                        {isKnownPlan && (
                          <button
                            type="button"
                            title={
                              draft.highlighted
                                ? "Quitar destacado global (afecta todos los nichos · guarda en sm_plans)"
                                : "Marcar como destacado global (afecta todos los nichos · guarda en sm_plans)"
                            }
                            disabled={savingHighlighted === pid}
                            onClick={() => void saveHighlighted(pid)}
                            className={cn(
                              "shrink-0 rounded p-0.5 transition-colors",
                              draft.highlighted
                                ? "text-amber-400 hover:text-amber-300"
                                : "text-muted-foreground/40 hover:text-amber-400",
                            )}
                          >
                            {savingHighlighted === pid
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Star className={cn("h-3.5 w-3.5", draft.highlighted && "fill-amber-400")} />
                            }
                          </button>
                        )}
                        <button
                          type="button"
                          title="Eliminar plan"
                          disabled={deletingTier === pid || tierIds.length <= 1}
                          onClick={() => void removeTier(pid)}
                          className="shrink-0 rounded p-0.5 text-muted-foreground/40 transition-colors hover:text-red-500 disabled:opacity-30"
                        >
                          {deletingTier === pid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      {!isTrial ? (
                        <div className="mt-1.5 flex items-center gap-1">
                          <span className="text-[11px] text-muted-foreground">$</span>
                          <input
                            type="number" min={0} step={100}
                            value={numStr(draft.priceArs)}
                            placeholder={numStr(PLAN_BASE[pid as KnownPlanId]?.priceArs)}
                            onChange={(e) => setPlanPrice(pid, e.target.value)}
                            className="w-full rounded border border-border/40 bg-background px-2 py-1 text-[12px] text-foreground placeholder:text-muted-foreground/40"
                          />
                          <span className="text-[10px] text-muted-foreground">ARS/mes</span>
                        </div>
                      ) : (
                        <div className="mt-1.5 text-[11px] font-semibold text-amber-400">Gratis · Trial</div>
                      )}
                      <button
                        type="button"
                        onClick={() => void savePlan(pid)}
                        disabled={saving === pid}
                        className={cn(
                          "mt-2 flex w-full items-center justify-center gap-1 rounded px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50",
                          draft.dirty
                            ? "bg-primary/20 text-primary hover:bg-primary/30"
                            : "bg-muted/30 text-muted-foreground hover:bg-muted/50",
                        )}
                      >
                        {saving === pid ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        Guardar
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {/* ── Sección: Límites & cuotas (igual a la matriz global) ── */}
              <tr className="cursor-pointer bg-muted/20 hover:bg-muted/30" onClick={() => toggleSection("Límites & cuotas")}>
                <td
                  colSpan={1 + tierIds.length}
                  className="sticky left-0 z-[1] px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
                >
                  <span className="flex items-center gap-1.5">
                    {collapsedSections.has("Límites & cuotas")
                      ? <ChevronRight className="h-3 w-3" />
                      : <ChevronDown className="h-3 w-3" />}
                    Límites &amp; cuotas
                  </span>
                </td>
              </tr>

              {/* Filas numéricas */}
              {!collapsedSections.has("Límites & cuotas") && (
                [
                  { key: "products",               name: "Productos",                   hint: "vacío = ∞" },
                  { key: "ordersPerMonth",          name: "Órdenes / mes",               hint: "vacío = ∞" },
                  { key: "locations",               name: "Sucursales",                  hint: "" },
                  { key: "categories",              name: "Categorías",                  hint: "" },
                  { key: "brands",                  name: "Marcas",                      hint: "" },
                  { key: "b2bUsers",                name: "Usuarios B2B",                hint: "" },
                  { key: "teamMembers",             name: "Miembros del equipo",         hint: "vacío = ∞" },
                  { key: "images",                  name: "Imágenes",                    hint: "" },
                  { key: "emailsPerDay",            name: "Emails / día",                hint: "Structura" },
                  { key: "pushPerDay",              name: "Push / día",                  hint: "" },
                  { key: "calendarSync",            name: "Calendar sync",               hint: "vacío = ∞" },
                  { key: "professionals",           name: "Profesionales",               hint: "vacío = ∞ · nicho servicios" },
                  { key: "bookingsPerMonth",        name: "Turnos / mes",                hint: "vacío = ∞ · nicho servicios" },
                  { key: "renataCreditsMonth",      name: "Créditos RENATA / mes",       hint: "null = ilimitado" },
                  { key: "renataCreditsExpiryDays", name: "Vencimiento créditos (días)", hint: "" },
                ] as { key: keyof LimitsOverride; name: string; hint: string }[]
              ).map(({ key, name, hint }, ri) => (
                <tr key={key} className={cn("border-b border-border/20 hover:bg-card/30", ri % 2 === 1 ? "bg-card/10" : "")}>
                  <td className={cn("sticky left-0 z-[1] px-3 py-2 shadow-[4px_0_8px_-4px_rgba(0,0,0,.1)]", ri % 2 === 1 ? "bg-card/60" : "bg-background")}>
                    <span className="text-[12px] text-foreground">{name}</span>
                    {hint && <span className="ml-1 text-[10px] text-muted-foreground/50">· {hint}</span>}
                  </td>
                  {tierIds.map((pid) => {
                    const draft = matrix[pid];
                    const lim = effectiveLimits(pid, draft);
                    const baseVal = (lim as Record<string, unknown>)[key as string];
                    const overrideVal = (draft.limits as Record<string, unknown>)[key as string];
                    const isOverridden = overrideVal !== undefined && overrideVal !== null;
                    return (
                      <td key={pid} className="border-l border-border/20 px-3 py-2">
                        <input
                          type="number" min={0}
                          value={numStr(overrideVal as number | null | undefined)}
                          placeholder={baseVal == null ? "∞" : String(baseVal)}
                          onChange={(e) => setPlanLimit(pid, key, e.target.value === "" ? null : Number(e.target.value))}
                          className={cn(
                            "w-full rounded border bg-background px-2 py-1 text-[12px] text-right text-foreground placeholder:text-muted-foreground/40",
                            isOverridden ? "border-primary/40 bg-primary/5" : "border-border/40",
                          )}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Filas booleanas */}
              {!collapsedSections.has("Límites & cuotas") && (
                [
                  { key: "storefront" as keyof LimitsOverride, name: "Storefront", defaultVal: true },
                  { key: "portalB2b"  as keyof LimitsOverride, name: "Portal B2B", defaultVal: false },
                ] as { key: keyof LimitsOverride; name: string; defaultVal: boolean }[]
              ).map(({ key, name, defaultVal }, ri) => (
                <tr key={key} className={cn("border-b border-border/20 hover:bg-card/30", ri % 2 === 0 ? "bg-card/10" : "")}>
                  <td className={cn("sticky left-0 z-[1] px-3 py-2 shadow-[4px_0_8px_-4px_rgba(0,0,0,.1)]", ri % 2 === 0 ? "bg-card/60" : "bg-background")}>
                    <span className="text-[12px] text-foreground">{name}</span>
                  </td>
                  {tierIds.map((pid) => {
                    const draft = matrix[pid];
                    const lim = effectiveLimits(pid, draft);
                    const val = (draft.limits as Record<string, unknown>)[key as string] !== undefined
                      ? Boolean((draft.limits as Record<string, unknown>)[key as string])
                      : Boolean((lim as Record<string, unknown>)[key as string] ?? defaultVal);
                    return (
                      <td key={pid} className="border-l border-border/20 px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={val}
                          onChange={(e) => setPlanLimit(pid, key, e.target.checked)}
                          className="h-4 w-4 cursor-pointer accent-primary"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* ── Sección: Features por grupo ── */}
              {FEATURE_GROUPS.map((group) => {
                const isCollapsed = collapsedSections.has(group.label);
                return (
                  <React.Fragment key={group.label}>
                    <tr
                      key={`g-${group.label}`}
                      className="cursor-pointer bg-muted/20 hover:bg-muted/30"
                      onClick={() => toggleSection(group.label)}
                    >
                      <td
                        colSpan={1 + tierIds.length}
                        className="sticky left-0 z-[1] px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
                      >
                        <span className="flex items-center gap-1.5">
                          {isCollapsed
                            ? <ChevronRight className="h-3 w-3" />
                            : <ChevronDown className="h-3 w-3" />}
                          {group.label}
                        </span>
                      </td>
                    </tr>
                    {!isCollapsed && group.ids.map((fid, ri) => {
                      const feat = FEATURE_BY_ID.get(fid);
                      if (!feat) return null;
                      const cfg = featureCfgs[fid];
                      const showInLanding = cfg?.showInLanding ?? true;
                      const anyChecked = tierIds.some((pid) => matrix[pid].featureIds.has(fid));
                      return (
                        <tr
                          key={fid}
                          className={cn(
                            "border-b border-border/20 transition-colors hover:bg-card/30",
                            ri % 2 === 1 ? "bg-card/10" : "",
                            !showInLanding && "opacity-50",
                          )}
                        >
                          {/* Feature name + label override */}
                          <td className={cn(
                            "sticky left-0 z-[1] px-3 py-2 shadow-[4px_0_8px_-4px_rgba(0,0,0,.1)]",
                            ri % 2 === 1 ? "bg-card/60" : "bg-background",
                          )}>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                title={showInLanding ? "Visible en landing" : "Oculto en landing"}
                                onClick={() => {
                                  const newVal = !showInLanding;
                                  const label = featureCfgs[fid]?.label || null;
                                  void apiSendJson("/api/admin/feature-config", "PATCH", {
                                    featureId: fid, label, showInLanding: newVal,
                                  }).then(() => {
                                    setFeatureCfgs((prev) => ({
                                      ...prev,
                                      [fid]: { ...prev[fid], showInLanding: newVal, dirty: false },
                                    }));
                                  }).catch(() => toast.error("Error al guardar visibilidad"));
                                }}
                                className={cn(
                                  "flex shrink-0 h-5 w-5 items-center justify-center rounded transition-colors",
                                  showInLanding
                                    ? "text-primary/70 hover:text-primary"
                                    : "text-muted-foreground/50 hover:text-muted-foreground",
                                )}
                              >
                                {showInLanding ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                              </button>
                              <div>
                                <div className={cn("text-[12px] leading-snug", anyChecked ? "text-foreground" : "text-muted-foreground/70")}>
                                  {feat.name}
                                </div>
                                {feat.lifecycle === "beta" && (
                                  <span className="rounded bg-amber-500/20 px-1 py-0.5 text-[9px] font-semibold text-amber-400">Beta</span>
                                )}
                              </div>
                            </div>
                            <div className="mt-1 flex items-center gap-1">
                              <Pencil className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                              <input
                                value={cfg?.label ?? ""}
                                placeholder={feat.name}
                                onChange={(e) => setFeatureCfgField(fid, "label", e.target.value)}
                                className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-[11px] text-foreground/70 placeholder:text-muted-foreground/30 hover:border-border/40 focus:border-primary/50 focus:outline-none"
                              />
                              {cfg?.dirty && (
                                <button
                                  type="button"
                                  onClick={() => void saveFeatureCfg(fid)}
                                  disabled={savingFeature === fid}
                                  className="shrink-0 rounded bg-amber-500/15 px-1 py-0.5 text-[10px] text-amber-400 hover:bg-amber-500/25"
                                >
                                  {savingFeature === fid ? <Loader2 className="h-2.5 w-2.5 animate-spin inline" /> : "↗"}
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Plan checkboxes */}
                          {tierIds.map((pid) => (
                            <td key={pid} className="border-l border-border/20 px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={matrix[pid].featureIds.has(fid)}
                                onChange={() => toggleFeature(pid, fid)}
                                className="h-4 w-4 cursor-pointer accent-primary"
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {/* ── Sección: COPILOT / RENATA ── */}
              <tr
                className="cursor-pointer bg-violet-500/10 hover:bg-violet-500/15"
                onClick={() => toggleSection("COPILOT / RENATA")}
              >
                <td
                  colSpan={1 + tierIds.length}
                  className="sticky left-0 z-[1] px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-violet-400"
                >
                  <span className="flex items-center gap-1.5">
                    {collapsedSections.has("COPILOT / RENATA")
                      ? <ChevronRight className="h-3 w-3" />
                      : <ChevronDown className="h-3 w-3" />}
                    <Bot className="h-3.5 w-3.5" />
                    Copilot / Renata
                    <span className="ml-1 rounded bg-violet-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-violet-300">
                      habilitar features por plan
                    </span>
                  </span>
                </td>
              </tr>

              {!collapsedSections.has("COPILOT / RENATA") && RENATA_COPILOT_FEATURES.map((feat, ri) => (
                <tr
                  key={feat.id}
                  className={cn(
                    "border-b border-border/20 hover:bg-card/30",
                    ri % 2 === 1 ? "bg-card/10" : "",
                  )}
                >
                  <td className={cn(
                    "sticky left-0 z-[1] px-3 py-2 shadow-[4px_0_8px_-4px_rgba(0,0,0,.1)]",
                    ri % 2 === 1 ? "bg-card/60" : "bg-background",
                  )}>
                    <div className="flex items-center gap-1.5">
                      <Bot className="h-3.5 w-3.5 shrink-0 text-violet-400/60" />
                      <div>
                        <div className="text-[12px] font-medium leading-snug text-foreground">{feat.label}</div>
                        <div className="text-[10px] text-muted-foreground/60">{feat.description}</div>
                      </div>
                      {!feat.mature && (
                        <span className="ml-auto rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-amber-400">Beta</span>
                      )}
                    </div>
                  </td>
                  {tierIds.map((pid) => {
                    const isEnabled = (copilotFlags[pid] ?? {})[feat.id] !== false;
                    const isDirty   = copilotDirty.has(pid);
                    return (
                      <td key={pid} className="border-l border-border/20 px-3 py-2">
                        <div className="flex flex-col items-center gap-1">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => toggleCopilotFlag(pid, feat.id)}
                            className="h-4 w-4 cursor-pointer accent-violet-500"
                          />
                          {isDirty && (
                            <span className="text-[9px] text-amber-400">•</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Guardar copilot por plan — fila inline */}
              {!collapsedSections.has("COPILOT / RENATA") && copilotDirty.size > 0 && (
                <tr className="bg-violet-500/5">
                  <td className="sticky left-0 z-[1] bg-violet-500/5 px-3 py-1.5 text-[11px] text-violet-400">
                    Cambios pendientes en Renata
                  </td>
                  {tierIds.map((pid) => (
                    <td key={pid} className="border-l border-border/20 px-3 py-1.5 text-center">
                      {copilotDirty.has(pid) && (
                        <button
                          type="button"
                          onClick={() => void saveCopilotPlan(pid)}
                          disabled={savingCopilot === pid}
                          className="flex items-center gap-1 rounded bg-violet-500/20 px-2 py-0.5 text-[11px] font-semibold text-violet-400 hover:bg-violet-500/30"
                        >
                          {savingCopilot === pid
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Save className="h-3 w-3" />}
                          Guardar
                        </button>
                      )}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
